// web/src/lib/schedule.js
// Shared shaping for the Schedule board: fold tasks, lessons and services —
// three different row shapes from three different tables — into one list of
// comparable items, and group them into the barn's four time blocks.
//
// Tasks carry an explicit time_block. Lessons only carry a start time, so we
// derive their block from the clock. The boundaries below are the barn's day,
// not arbitrary: morning runs to late morning, midday is the lesson/ride window,
// afternoon is the second shift, and anything after 8pm is night check.

import { fmtDateOpts } from './dates';

export const BLOCKS = [
  ['am', 'Morning'],
  ['noon', 'Midday'],
  ['pm', 'Afternoon'],
  ['nc', 'Night check'],
  ['none', 'Anytime'],
];

const BLOCK_ORDER = BLOCKS.reduce((acc, [id], i) => ({ ...acc, [id]: i }), {});

/** "HH:MM" → block id. Anything unparseable falls through to "Anytime". */
export function blockForTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm ?? '');
  if (!m) return 'none';
  const h = Number(m[1]);
  if (h < 11) return 'am';
  if (h < 14) return 'noon';
  if (h < 20) return 'pm';
  return 'nc';
}

/** A task row from /schedule or /tasks → a board item. */
export function taskItem(t) {
  const detail = [t.taskType, t.rideType].filter(Boolean).join(' · ');
  return {
    key: `task:${t.id}`,
    kind: 'task',
    id: t.id,
    date: t.date,
    time: t.time ?? null,
    block: t.timeBlock ?? 'none',
    title: t.horseName ?? 'Barn-wide',
    detail,
    person: t.assignedName ?? null,
    // Filter targets offered on the row: a task belongs to whoever it's on.
    people: refs([['u', t.assignedUserId, t.assignedName]]),
    status: t.status,
    done: t.status === 'done',
    muted: t.status === 'skipped' || t.status === 'cancelled',
    horseId: t.horseId ?? null,
    raw: t,
  };
}

/** A lesson row from /schedule or /lessons → a board item. */
export function lessonItem(l) {
  // Prefer the barn's own lesson type; fall back to the legacy free-text column
  // for anything created before types existed.
  const type = l.lessonTypeName ?? (l.lessonType ?? 'lesson').replace(/_/g, ' ');
  const detail = [type, l.horseName].filter(Boolean).join(' · ');
  return {
    key: `lesson:${l.id}`,
    kind: 'lesson',
    // The color token the barn chose for this type; the spine reads it.
    lessonTypeColor: l.lessonTypeColor ?? null,
    lessonTypeId: l.lessonTypeId ?? null,
    id: l.id,
    date: l.date,
    time: l.startTime ?? null,
    block: blockForTime(l.startTime),
    title: l.riderName,
    detail,
    person: l.trainerName ?? null,
    // A lesson belongs to two people, and either is a reasonable thing to
    // pivot on — "show me Ava's week", "show me Sam's sessions".
    people: refs([
      ['p', l.riderPersonId, l.riderName],
      ['u', l.trainerUserId, l.trainerName],
    ]),
    status: l.status,
    done: l.status === 'completed',
    muted: l.status === 'cancelled',
    horseId: l.horseId ?? null,
    raw: l,
  };
}

const SERVICE_LABEL = {
  coggins: 'Coggins',
  vaccination: 'Vaccination',
  farrier: 'Farrier',
  dental: 'Dental',
  vet: 'Vet',
  injection: 'Injection',
  injury: 'Injury',
  deworming: 'Deworming',
  other: 'Service',
};

/**
 * A service falling due — the farrier on Tuesday, shots on Friday.
 *
 * Read-only on the board: there is nothing to tick off, because a due date is
 * a prediction rather than an appointment, and it stops being due when
 * somebody records the visit on the horse. So it has no checkbox and tapping
 * it goes to the horse rather than opening an editor.
 */
export function serviceItem(s) {
  return {
    key: `service:${s.id}`,
    kind: 'service',
    id: s.id,
    date: s.date,
    time: null,
    block: 'none',
    title: SERVICE_LABEL[s.recordType] ?? 'Service',
    detail: [s.horseName, s.providerName].filter(Boolean).join(' · '),
    person: null,
    people: [],
    serviceType: s.recordType,
    status: 'due',
    done: false,
    muted: false,
    horseId: s.horseId ?? null,
    raw: s,
  };
}

/**
 * All items for one ISO date, in reading order: block first, then clock time
 * (untimed last within a block), then title.
 */
export function itemsForDate(date, tasks = [], lessons = [], services = []) {
  const items = [
    ...tasks.filter((t) => t.date === date).map(taskItem),
    ...lessons.filter((l) => l.date === date).map(lessonItem),
    ...services.filter((v) => v.date === date).map(serviceItem),
  ];
  return items.sort(compareItems);
}

/** The same items, split into the blocks that actually have something in them. */
export function groupByBlock(items) {
  return BLOCKS.map(([id, label]) => ({
    id,
    label,
    items: items.filter((i) => i.block === id),
  })).filter((g) => g.items.length > 0);
}

function compareItems(a, b) {
  const block = (BLOCK_ORDER[a.block] ?? 99) - (BLOCK_ORDER[b.block] ?? 99);
  if (block !== 0) return block;
  // Untimed items sit after timed ones rather than sorting as "00:00".
  if (a.time && b.time && a.time !== b.time) return a.time < b.time ? -1 : 1;
  if (a.time && !b.time) return -1;
  if (!a.time && b.time) return 1;
  return String(a.title ?? '').localeCompare(String(b.title ?? ''));
}

/** Monday of the ISO week containing `iso`. Pure string math (no timezone). */
export function weekStart(iso) {
  const day = new Date(`${iso}T00:00:00Z`).getUTCDay(); // 0=Sun..6=Sat
  return addDaysISO(iso, -((day + 6) % 7));
}

export function addDaysISO(iso, n) {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** First of the month containing `iso`. */
export function monthStart(iso) {
  return `${iso.slice(0, 7)}-01`;
}

/** First of the month `n` months from the one containing `iso`. */
export function addMonthsISO(iso, n) {
  const d = new Date(`${monthStart(iso)}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + n);
  return d.toISOString().slice(0, 10);
}

/**
 * The Mon–Sun weeks a month calendar needs to show — five rows most months,
 * six when the month straddles them. Returned as { from, to, weeks }, where
 * `from`/`to` are exactly what to ask /schedule for, so the leading and
 * trailing days from the neighbouring months aren't blank.
 */
export function monthGrid(iso) {
  const first = monthStart(iso);
  const from = weekStart(first);
  const daysInMonth = new Date(
    Date.UTC(Number(first.slice(0, 4)), Number(first.slice(5, 7)), 0),
  ).getUTCDate();
  const lead = daysBetween(from, first);
  const rows = Math.ceil((lead + daysInMonth) / 7);
  const weeks = Array.from({ length: rows }, (_, w) =>
    Array.from({ length: 7 }, (_, i) => addDaysISO(from, w * 7 + i)),
  );
  return { from, to: addDaysISO(from, rows * 7 - 1), weeks };
}

function daysBetween(a, b) {
  return Math.round(
    (new Date(`${b}T00:00:00Z`) - new Date(`${a}T00:00:00Z`)) / 86_400_000,
  );
}

// ---------------------------------------------------------------------------
// Filtering by person
//
// Two id spaces meet on this board: tasks and lessons point at `users` (the
// assignee, the trainer), while riders are `barn_people`. A filter value is
// namespaced — "u:<userId>" or "p:<personId>" — so one dropdown can offer both
// without the two ever colliding.
// ---------------------------------------------------------------------------

export function personKey(kind, id) {
  return `${kind}:${id}`;
}

/** [kind, id, name] triples → filter chips, dropping the ones with no person. */
function refs(triples) {
  return triples
    .filter(([, id, name]) => id && name)
    .map(([kind, id, name]) => ({ ref: personKey(kind, id), name }));
}

// ---------------------------------------------------------------------------
// Filtering, in two axes: who it's for and what kind of thing it is.
//
// Both are strings in the URL, so a filtered week is a link. Both are applied
// to the same item list the views already build, rather than re-querying — a
// week is a few dozen rows and the counts on the lozenges need the unfiltered
// set anyway.
// ---------------------------------------------------------------------------

/** The roles a board groups people into, in the order the chips read. */
export const ROLE_GROUPS = [
  ['trainer', 'Trainers'],
  ['groom', 'Grooms'],
  ['manager', 'Managers'],
  ['staff', 'Staff'],
  ['rider', 'Riders'],
  ['none', 'Unassigned'],
];

/**
 * Which role bucket an item's people fall in.
 *
 * Roles come from the team list, which knows a groom from a manager. A rider
 * is a barn_person rather than a member and has no role at all, so they get
 * their own bucket — on a board "who is riding" is the same kind of question
 * as "who is mucking out", whatever the accounts say.
 */
export function itemRoles(item, roleByUserId) {
  const roles = new Set();
  for (const p of item.people ?? []) {
    const [kind, id] = splitKey(p.ref);
    if (kind === 'p') {
      roles.add('rider');
      continue;
    }
    if (kind !== 'u') continue;
    // The work says what they are here. Somebody attached to a lesson as its
    // trainer IS the trainer of it, whatever their account role, and this is
    // the only signal available to a trainer or a groom — /team is owner and
    // manager only, so for everyone else roleByUserId is empty. Falling back
    // to a generic "Staff" is honest; filing them all under Managers was not.
    const known = roleByUserId?.[id];
    if (known) roles.add(known);
    else if (item.kind === 'lesson') roles.add('trainer');
    else roles.add('staff');
  }
  // A task with nobody's name on it is the most actionable thing on a barn
  // board, so it is a bucket rather than an absence.
  if (roles.size === 0 && item.kind === 'task') roles.add('none');
  return roles;
}

/** The activity a filter chip matches: one lesson type, one task type, or a service. */
export function activityKey(item) {
  if (item.kind === 'lesson') return `lesson:${item.lessonTypeId ?? 'other'}`;
  if (item.kind === 'service') return `service:${item.serviceType ?? 'other'}`;
  return `task:${item.raw?.taskType ?? 'other'}`;
}

/** How that key reads on a chip. */
export function activityLabel(item) {
  if (item.kind === 'lesson') {
    return item.raw?.lessonTypeName ?? (item.raw?.lessonType ?? 'Lesson').replace(/_/g, ' ');
  }
  if (item.kind === 'service') return item.title;
  const t = item.raw?.taskType ?? 'other';
  return t.replace(/_/g, ' ');
}

/** Does this task belong to `who` ("u:…"/"p:…")? Riders never own tasks. */
export function taskMatchesPerson(t, who) {
  if (!who) return true;
  const [kind, id] = splitKey(who);
  return kind === 'u' && t.assignedUserId === id;
}

/** A lesson is "theirs" whether they ride it or run it. */
export function lessonMatchesPerson(l, who) {
  if (!who) return true;
  const [kind, id] = splitKey(who);
  return kind === 'u' ? l.trainerUserId === id : l.riderPersonId === id;
}

function splitKey(who) {
  const i = who.indexOf(':');
  return i === -1 ? [null, null] : [who.slice(0, i), who.slice(i + 1)];
}

export function formatDay(iso, opts = {}) {
  if (!iso) return '';
  return fmtDateOpts(iso, opts);
}

/** "Mon Aug 24 – Sun Aug 30", collapsing the month when both ends share one. */
export function formatRange(from, to) {
  if (!from || !to) return '';
  const sameMonth = from.slice(0, 7) === to.slice(0, 7);
  const left = formatDay(from, { month: 'short', day: 'numeric' });
  const right = formatDay(to, sameMonth ? { day: 'numeric' } : { month: 'short', day: 'numeric' });
  return `${left} – ${right}, ${from.slice(0, 4)}`;
}

/** "7:30 AM" from "07:30". Returns '' for untimed items. */
export function formatTime(hhmm) {
  const m = /^(\d{1,2}):(\d{2})/.exec(hhmm ?? '');
  if (!m) return '';
  let h = Number(m[1]);
  const ampm = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ampm}`;
}
